import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { 
  Plus, FileText, Send, Eye, CheckCircle, XCircle, Clock, 
  Calendar, Pound, Car, User, Edit, Trash2, Search,
  Download, RefreshCw, AlertCircle, Briefcase, Printer,
  Ban, RotateCcw, AlertTriangle, MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';
import QuotePrintView from './QuotePrintView';
import WorkflowDiagram from './WorkflowDiagram';
import ChangeStatusDialog from './ChangeStatusDialog';
import StatusBadge from './StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuotesManagement = ({ getAuthHeaders }) => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isPrintViewOpen, setIsPrintViewOpen] = useState(false);
  const [printQuote, setPrintQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('draft,sent'); // Default to show draft and sent quotes
  const [customerFilter, setCustomerFilter] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isCustomerSelectOpen, setIsCustomerSelectOpen] = useState(false);
  const customerSearchRef = React.useRef(null);
  const [allQuotes, setAllQuotes] = useState([]); // Store all quotes for accurate counts
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [serviceSearchOpen, setServiceSearchOpen] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  
  // New reversible workflow states
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [quoteToChangeStatus, setQuoteToChangeStatus] = useState(null);
  const [selectedLineIndex, setSelectedLineIndex] = useState(null);
  
  // Status management state
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [selectedQuoteForStatus, setSelectedQuoteForStatus] = useState(null);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [statusChangeReason, setStatusChangeReason] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    customer_id: '',
    contact_id: '',
    vehicle_ids: [],
    items: [],
    notes_public: '',
    notes_internal: '',
    valid_until: '',
    deposit_required: false,
    deposit_type: 'percent',
    deposit_value: 0,
    currency: 'GBP'
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Focus search input when customer select opens
  useEffect(() => {
    if (isCustomerSelectOpen && customerSearchRef.current) {
      // Small delay to ensure the dropdown is fully rendered
      setTimeout(() => {
        customerSearchRef.current?.focus();
      }, 100);
    }
  }, [isCustomerSelectOpen]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchQuotes();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, statusFilter, customerFilter]);

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchQuotes(),
        fetchCustomers(),
        fetchServices()
      ]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      // Fetch all quotes first for accurate counts
      const allResponse = await axios.get(`${API}/quotes`, getAuthHeaders());
      // Store all quotes excluding accepted (which belong in Orders)
      const allQuotesData = allResponse.data.filter(q => q.status !== 'accepted');
      setAllQuotes(allQuotesData);
      
      // Apply filters for display
      let filteredQuotes = [...allQuotesData];
      
      // Apply status filter
      if (statusFilter && statusFilter !== 'all') {
        if (statusFilter.includes(',')) {
          // Multi-status filter (e.g., "draft,sent")
          const statuses = statusFilter.split(',');
          filteredQuotes = filteredQuotes.filter(q => statuses.includes(q.status));
        } else {
          // Single status filter
          filteredQuotes = filteredQuotes.filter(q => q.status === statusFilter);
        }
      }
      
      // Apply customer filter
      if (customerFilter && customerFilter !== 'all') {
        filteredQuotes = filteredQuotes.filter(q => q.customer_id === customerFilter);
      }
      
      setQuotes(filteredQuotes);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      toast.error('Failed to fetch quotes');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, getAuthHeaders());
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  // Filtered and sorted customers for dropdown
  const filteredCustomers = React.useMemo(() => {
    let filtered = [...customers];
    
    // Filter by search term
    if (customerSearchTerm) {
      filtered = filtered.filter(customer => 
        customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.phone?.includes(customerSearchTerm)
      );
    }
    
    // Sort alphabetically by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    
    return filtered;
  }, [customers, customerSearchTerm]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, getAuthHeaders());
      console.log('Services fetched:', response.data);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  // Fetch contacts for selected customer
  const fetchContactsForCustomer = async (customerId) => {
    try {
      console.log('Fetching contacts for customer:', customerId);
      const response = await axios.get(`${API}/customers/${customerId}/contacts`, getAuthHeaders());
      console.log('Contacts response:', response.data);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts for this customer');
      setContacts([]);
    }
  };

  // Fetch vehicles for selected customer
  const fetchVehiclesForCustomer = async (customerId) => {
    try {
      console.log('Fetching vehicles for customer:', customerId);
      const response = await axios.get(`${API}/customers/${customerId}/vehicles`, getAuthHeaders());
      console.log('Vehicles response:', response.data);
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to load vehicles for this customer');
      setVehicles([]);
    }
  };

  // Handle customer selection change
  const handleCustomerChange = async (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    setFormData({ 
      ...formData, 
      customer_id: customerId,
      contact_id: '',  // Reset contact
      vehicle_ids: []  // Reset vehicles
    });
    
    // Fetch contacts and vehicles for this customer
    if (customerId) {
      await fetchContactsForCustomer(customerId);
      await fetchVehiclesForCustomer(customerId);
    } else {
      setContacts([]);
      setVehicles([]);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      contact_id: '',
      vehicle_ids: [],
      items: [],
      notes_public: '',
      notes_internal: '',
      valid_until: '',
      deposit_required: false,
      deposit_type: 'percent',
      deposit_value: 0,
      currency: 'GBP'
    });
    setEditingQuote(null);
    setCustomerSearchTerm('');
    setSelectedCustomer(null);
    setContacts([]);
    setVehicles([]);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = async (quote) => {
    setEditingQuote(quote);
    
    // Find and set the selected customer
    const customer = customers.find(c => c.id === quote.customer_id);
    setSelectedCustomer(customer);
    
    // Fetch contacts and vehicles for this customer
    if (quote.customer_id) {
      await fetchContactsForCustomer(quote.customer_id);
      await fetchVehiclesForCustomer(quote.customer_id);
    }
    
    setFormData({
      customer_id: quote.customer_id,
      contact_id: quote.contact_id || '',
      vehicle_ids: quote.vehicles?.map(v => v.vehicle_id) || [],
      items: quote.items.map(item => ({
        service_id: item.service_id,
        vehicle_id: item.vehicle_id,
        sku: item.sku || '',
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        cost_price: item.cost_price || 0,
        duration: item.duration || null,
        discount_value: item.discount_value || 0,
        tax_rate: item.tax_rate || 0
      })),
      notes_public: quote.notes_public || '',
      notes_internal: quote.notes_internal || '',
      valid_until: quote.valid_until ? new Date(quote.valid_until).toISOString().split('T')[0] : '',
      deposit_required: quote.deposit_required,
      deposit_type: quote.deposit_type,
      deposit_value: quote.deposit_value || 0,
      currency: quote.currency
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        // Ensure contact_id is not '__primary__' placeholder
        contact_id: formData.contact_id === '__primary__' ? null : formData.contact_id
      };

      console.log('Submitting quote data:', submitData);
      console.log('Form items:', formData.items);

      if (editingQuote) {
        await axios.put(`${API}/quotes/${editingQuote.id}`, submitData, getAuthHeaders());
        toast.success('Quote updated successfully');
      } else {
        await axios.post(`${API}/quotes`, submitData, getAuthHeaders());
        toast.success('Quote created successfully');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      console.error('Quote operation error:', error);
      console.error('Error details:', error.response?.data);
      let errorMessage = 'Operation failed';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/send`, {}, getAuthHeaders());
      toast.success('Quote sent successfully');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  const handleConvertToJob = async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/convert-to-jobs`, {}, getAuthHeaders());
      toast.success('Quote converted to job successfully');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to convert quote to job');
    }
  };

  const openStatusDialog = async (quote) => {
    try {
      setSelectedQuoteForStatus(quote);
      
      // Fetch available statuses for this quote
      const response = await axios.get(`${API}/quotes/${quote.id}/available-statuses`, getAuthHeaders());
      setAvailableStatuses(response.data.available_transitions);
      
      setIsStatusDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load available statuses');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedQuoteForStatus) return;
    
    try {
      const statusData = {
        status: newStatus,
        reason: statusChangeReason
      };
      
      await axios.put(`${API}/quotes/${selectedQuoteForStatus.id}/status`, statusData, getAuthHeaders());
      await fetchQuotes();
      
      setIsStatusDialogOpen(false);
      setSelectedQuoteForStatus(null);
      setStatusChangeReason('');
      
      toast.success(`Quote status updated to ${newStatus}`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update quote status';
      toast.error(errorMessage);
    }
  };

  // Legacy function for backward compatibility
  const handleQuickStatusChange = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { status: newStatus }, getAuthHeaders());
      toast.success(`Quote ${newStatus} successfully`);
      fetchQuotes();
    } catch (error) {
      toast.error(`Failed to ${newStatus} quote`);
    }
  };

  const openDeleteConfirmation = (quote) => {
    setQuoteToDelete(quote);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteQuote = async () => {
    if (!quoteToDelete) return;
    
    try {
      await axios.delete(`${API}/quotes/${quoteToDelete.id}`, getAuthHeaders());
      toast.success('Quote deleted successfully');
      setDeleteConfirmOpen(false);
      setQuoteToDelete(null);
      fetchQuotes();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete quote');
    }
  };

  // Handle status change with reversibility
  const handleChangeStatus = async (newStatus, reason) => {
    if (!quoteToChangeStatus) return;
    
    try {
      await axios.put(
        `${API}/quotes/${quoteToChangeStatus.id}`, 
        { 
          status: newStatus,
          status_change_reason: reason || undefined
        }, 
        getAuthHeaders()
      );
      
      toast.success(`Quote status changed to ${newStatus}`);
      setChangeStatusDialogOpen(false);
      setQuoteToChangeStatus(null);
      fetchQuotes();
    } catch (error) {
      console.error('Status change error:', error);
      toast.error('Failed to change status');
    }
  };

  const openChangeStatusDialog = (quote) => {
    setQuoteToChangeStatus(quote);
    setChangeStatusDialogOpen(true);
  };

  // Available statuses for quotes
  const quoteStatuses = ['draft', 'sent', 'accepted', 'declined', 'archived'];

  const addLineItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        service_id: null, // Use null instead of empty string
        vehicle_id: null,
        sku: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        cost_price: 0,
        duration: null,
        discount_value: 0,
        tax_rate: 20
      }]
    });
  };

  const removeLineItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const getStatusBadge = (status, onClick = null) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-700', icon: FileText },
      sent: { color: 'bg-blue-100 text-blue-700', icon: Send },
      accepted: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      declined: { color: 'bg-red-100 text-red-700', icon: XCircle },
      expired: { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
      cancelled: { color: 'bg-gray-100 text-gray-600', icon: Ban },
      converted: { color: 'bg-purple-100 text-purple-700', icon: Briefcase }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const IconComponent = config.icon;

    const badgeContent = (
      <Badge className={`${config.color} flex items-center gap-1 ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}>
        <IconComponent className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );

    return onClick ? (
      <div onClick={onClick} className="inline-block">
        {badgeContent}
      </div>
    ) : badgeContent;
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const filteredQuotes = quotes.filter(quote => {
    if (searchTerm) {
      const customer = customers.find(c => c.id === quote.customer_id);
      const customerName = customer?.name || '';
      return (
        quote.quote_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Loading quotes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>
            Quotes
          </h2>
          <p className="text-gray-500">Create and manage customer quotes</p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600">
          <Plus className="w-4 h-4" />
          Create Quote
        </Button>
      </div>

      {/* Workflow Diagram */}
      <WorkflowDiagram currentStage="quote" />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        {['draft', 'sent', 'declined', 'expired', 'cancelled', 'converted'].map(status => {
          const count = allQuotes.filter(q => q.status === status).length;
          const isActive = statusFilter === status;
          return (
            <Card 
              key={status} 
              className={`p-4 cursor-pointer transition-all hover:shadow-md ${isActive ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setStatusFilter(status)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{status.charAt(0).toUpperCase() + status.slice(1)}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
                {getStatusBadge(status)}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search quotes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All Statuses">
                {statusFilter === 'draft,sent' ? 'Draft & Sent' : statusFilter === 'all' ? 'All Statuses' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft,sent">Draft & Sent (Default)</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>

          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All Customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(customer => (
                <SelectItem key={customer.id} value={customer.id}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={() => {
            setSearchTerm('');
            setStatusFilter('all');
            setCustomerFilter('all');
          }}>
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Quotes Table */}
      {filteredQuotes.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No quotes found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || statusFilter || customerFilter
              ? 'No quotes match your current filters.'
              : 'Get started by creating your first quote.'
            }
          </p>
          {!searchTerm && !statusFilter && !customerFilter && (
            <Button onClick={openCreateDialog} className="gap-2">
              <Plus className="w-4 h-4" />
              Create Your First Quote
            </Button>
          )}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quote #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuotes.map((quote) => {
                const customer = customers.find(c => c.id === quote.customer_id);
                
                // Calculate total duration from quote items
                const totalDuration = quote.items?.reduce((sum, item) => {
                  const itemDuration = item.duration || 0;
                  const itemQuantity = item.quantity || 1;
                  return sum + (itemDuration * itemQuantity);
                }, 0) || 0;
                
                return (
                  <TableRow key={quote.id}>
                    <TableCell>
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                        {quote.quote_number}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{customer?.name || 'Unknown Customer'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {quote.contact_name ? (
                        <span className="text-sm">{quote.contact_name}</span>
                      ) : (
                        <span className="text-gray-400 text-sm">No contact</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {quote.vehicles && quote.vehicles.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {quote.vehicles.map((vehicle, idx) => (
                            <code key={idx} className="bg-blue-50 px-2 py-0.5 rounded text-xs font-mono">
                              {vehicle.registration_number}
                            </code>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">No vehicle</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {totalDuration > 0 ? (
                        <div className="text-sm">
                          <Clock className="w-3 h-3 inline mr-1 text-gray-400" />
                          {totalDuration} min
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-green-600">
                        {formatCurrency(quote.total, quote.currency)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge 
                        status={quote.status} 
                        onClick={() => openChangeStatusDialog(quote)}
                      />
                    </TableCell>
                    <TableCell>
                      {quote.valid_until ? (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span className="text-sm">
                            {new Date(quote.valid_until).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">No expiry</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Primary Actions - Always Visible */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedQuote(quote);
                            setIsViewDialogOpen(true);
                          }}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPrintQuote(quote);
                            setIsPrintViewOpen(true);
                          }}
                          title="Print/Email Quote"
                          className="text-purple-600"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>

                        {/* Secondary Actions - Dropdown Menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => openEditDialog(quote)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Quote
                            </DropdownMenuItem>
                            
                            <DropdownMenuItem onClick={() => openChangeStatusDialog(quote)}>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Change Status
                            </DropdownMenuItem>

                            {quote.status === 'draft' && (
                              <DropdownMenuItem 
                                onClick={() => handleSendQuote(quote.id)}
                                className="text-blue-600"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Send to Customer
                              </DropdownMenuItem>
                            )}

                            {quote.status === 'sent' && (
                              <DropdownMenuItem 
                                onClick={() => handleSendQuote(quote.id)}
                                className="text-blue-600"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Resend Quote
                              </DropdownMenuItem>
                            )}

                            <DropdownMenuSeparator />
                            
                            <DropdownMenuItem 
                              onClick={() => openDeleteConfirmation(quote)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Quote Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-[98vw] max-h-[98vh] overflow-y-auto w-[98vw]">
          <DialogHeader>
            <DialogTitle>{editingQuote ? 'Edit Quote' : 'Create New Quote'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer">Customer *</Label>
                  <Select 
                    value={formData.customer_id} 
                    onValueChange={handleCustomerChange}
                    onOpenChange={setIsCustomerSelectOpen}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select or search customer..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px]">
                      <div className="sticky top-0 bg-white p-2 border-b z-10">
                        <Input
                          ref={customerSearchRef}
                          placeholder="Type to search customers..."
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="h-8"
                        />
                      </div>
                      <div className="max-h-[300px] overflow-y-auto">
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map(customer => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {customer.customer_type === 'business' ? '🏢' : '👤'} {customer.name}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="p-4 text-sm text-gray-500 text-center">
                            {customerSearchTerm ? 'No customers found matching your search' : 'No customers available'}
                          </div>
                        )}
                      </div>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''} available
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="valid_until">Valid Until</Label>
                  <Input
                    id="valid_until"
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  />
                </div>
              </div>

              {/* Contact and Vehicle Selection - Shows after customer selected */}
              {formData.customer_id && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact">
                      Contact {selectedCustomer?.customer_type === 'business' ? '*' : '(Optional)'}
                    </Label>
                    <Select 
                      value={formData.contact_id} 
                      onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
                      required={selectedCustomer?.customer_type === 'business'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={
                          selectedCustomer?.customer_type === 'business' 
                            ? "Select contact" 
                            : `${selectedCustomer?.name} (Primary)`
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedCustomer?.customer_type === 'individual' && (
                          <SelectItem value="__primary__">
                            {selectedCustomer?.name} (Primary Contact)
                          </SelectItem>
                        )}
                        {contacts.map(contact => (
                          <SelectItem key={contact.id} value={contact.id}>
                            {contact.first_name} {contact.last_name}
                            {contact.role && ` (${contact.role})`}
                          </SelectItem>
                        ))}
                        {contacts.length === 0 && selectedCustomer?.customer_type === 'business' && (
                          <SelectItem value="__no_contacts__" disabled>No contacts available - Please add a contact first</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {selectedCustomer?.customer_type === 'business' && (
                      <p className="text-xs text-gray-500 mt-1">Required for business customers</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="vehicle">Vehicle (Optional)</Label>
                    <Select 
                      value={formData.vehicle_ids[0] || '__none__'} 
                      onValueChange={(value) => {
                        if (value === '__none__') {
                          setFormData({ ...formData, vehicle_ids: [] });
                        } else {
                          setFormData({ ...formData, vehicle_ids: [value] });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select vehicle or none" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          No vehicle / Not applicable
                        </SelectItem>
                        {vehicles.map(vehicle => (
                          <SelectItem key={vehicle.id} value={vehicle.id}>
                            {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                          </SelectItem>
                        ))}
                        {vehicles.length === 0 && (
                          <SelectItem value="__none__">No vehicles on record</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Leave as "No vehicle" if not applicable</p>
                  </div>
                </div>
              )}
            </div>

            {/* Line Items */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <Label className="text-base font-medium">Quote Items</Label>
                <Button type="button" onClick={addLineItem} size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Item
                </Button>
              </div>
              
              {/* Proper Table Layout */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1400px] border-collapse">
                  {/* Table Header */}
                  {formData.items.length > 0 && (
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-3 text-sm font-medium text-gray-600 border-b w-24">Part #</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600 border-b w-1/6">Service</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-600 border-b w-1/6">Description</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600 border-b w-16">Qty</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600 border-b w-20">Price (£)</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600 border-b w-20">Cost (£)</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600 border-b w-20">Duration (min)</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600 border-b w-20">Discount (£)</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600 border-b w-16">Tax%</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600 border-b w-24">Line Total</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-600 border-b w-20">GP (£)</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600 border-b w-12"></th>
                      </tr>
                    </thead>
                  )}

                  {/* Table Body */}
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        {/* Part Number / SKU */}
                        <td className="p-3">
                          <Input
                            type="text"
                            value={item.sku || ''}
                            onChange={(e) => {
                              const sku = e.target.value;
                              updateLineItem(index, 'sku', sku);
                              
                              // Auto-lookup service by SKU
                              const service = services.find(s => s.sku && s.sku.toLowerCase() === sku.toLowerCase());
                              if (service) {
                                const newItems = [...formData.items];
                                newItems[index] = {
                                  ...newItems[index],
                                  service_id: service.id,
                                  sku: service.sku,
                                  description: service.description || service.name,
                                  unit_price: service.default_price || 0,
                                  cost_price: service.cost || 0,
                                  duration: service.default_duration_minutes || null
                                };
                                setFormData({ ...formData, items: newItems });
                              }
                            }}
                            className="h-9 text-sm w-full font-mono text-xs"
                            placeholder="SKU"
                            title="Enter part number to auto-fill"
                          />
                        </td>
                        
                        {/* Service Selection */}
                        <td className="p-3 relative">
                          <div className="flex gap-1">
                            <Select 
                            value={item.service_id === null ? 'custom' : item.service_id || 'custom'} 
                            onValueChange={(value) => {
                              // Batch all updates into a single state change to avoid race conditions
                              const newItems = [...formData.items];
                              
                              if (value !== 'custom') {
                                const service = services.find(s => s.id === value);
                                if (service) {
                                  const description = service.description || service.name;
                                  
                                  // Update all fields at once including cost and duration
                                  newItems[index] = {
                                    ...newItems[index],
                                    service_id: value,
                                    sku: service.sku || '',
                                    description: description,
                                    unit_price: service.default_price || 0,
                                    cost_price: service.cost || 0,
                                    duration: service.default_duration_minutes || null
                                  };
                                } else {
                                  newItems[index] = { ...newItems[index], service_id: value };
                                }
                              } else {
                                // Clear fields for custom item
                                newItems[index] = {
                                  ...newItems[index],
                                  service_id: null,
                                  sku: '',
                                  description: '',
                                  unit_price: 0,
                                  cost_price: 0,
                                  duration: null
                                };
                              }
                              
                              setFormData({ ...formData, items: newItems });
                            }}
                          >
                            <SelectTrigger className="h-9 text-sm w-full">
                              <SelectValue placeholder="Select service">
                                {item.service_id ? (
                                  <span className="truncate">
                                    {services.find(s => s.id === item.service_id)?.name || 'Select service'}
                                  </span>
                                ) : (
                                  'Custom Item'
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="max-w-md">
                              <SelectItem value="custom">Custom Item</SelectItem>
                              {services.map(service => (
                                <SelectItem key={service.id} value={service.id}>
                                  <div className="flex flex-col items-start py-1">
                                    <div className="font-medium">{service.name}</div>
                                    <div className="text-xs text-gray-500">
                                      {formatCurrency(service.default_price || 0)}
                                      {service.description && ` • ${service.description.substring(0, 40)}${service.description.length > 40 ? '...' : ''}`}
                                    </div>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-9 px-2 shrink-0"
                              onClick={() => {
                                setSelectedLineIndex(index);
                                setServiceSearchOpen(true);
                              }}
                              title="Search Services"
                            >
                              <Search className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>

                        {/* Description */}
                        <td className="p-3">
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            placeholder="Service description"
                            className="h-9 text-sm w-full"
                            required
                          />
                        </td>

                        {/* Quantity */}
                        <td className="p-3">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="h-9 text-sm text-center w-full"
                          />
                        </td>

                        {/* Unit Price */}
                        <td className="p-3">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="h-9 text-sm text-right w-full"
                            placeholder="0.00"
                          />
                        </td>

                        {/* Cost Price */}
                        <td className="p-3">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.cost_price || 0}
                            onChange={(e) => updateLineItem(index, 'cost_price', parseFloat(e.target.value) || 0)}
                            className="h-9 text-sm text-right w-full bg-yellow-50"
                            placeholder="0.00"
                            title="Internal cost (not shown to customer)"
                          />
                        </td>

                        {/* Duration */}
                        <td className="p-3">
                          <Input
                            type="number"
                            min="0"
                            value={item.duration || ''}
                            onChange={(e) => updateLineItem(index, 'duration', e.target.value ? parseInt(e.target.value) : null)}
                            className="h-9 text-sm text-center w-full bg-blue-50"
                            placeholder="-"
                            title="Duration in minutes"
                          />
                        </td>

                        {/* Discount */}
                        <td className="p-3">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.discount_value}
                            onChange={(e) => updateLineItem(index, 'discount_value', parseFloat(e.target.value) || 0)}
                            className="h-9 text-sm text-right w-full"
                            placeholder="0.00"
                          />
                        </td>

                        {/* Tax Rate */}
                        <td className="p-3">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={item.tax_rate}
                            onChange={(e) => updateLineItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                            className="h-9 text-sm text-center w-full"
                          />
                        </td>

                        {/* Line Total */}
                        <td className="p-3 text-right">
                          <div className="bg-gray-50 rounded px-3 py-2 text-sm font-medium">
                            {formatCurrency(
                              ((item.quantity * item.unit_price) - item.discount_value) * 
                              (1 + (item.tax_rate / 100))
                            )}
                          </div>
                        </td>

                        {/* Gross Profit */}
                        <td className="p-3 text-right">
                          <div className={`rounded px-3 py-2 text-sm font-medium ${
                            ((item.quantity * item.unit_price) - item.discount_value) - (item.quantity * (item.cost_price || 0)) >= 0 
                              ? 'bg-green-50 text-green-700' 
                              : 'bg-red-50 text-red-700'
                          }`}>
                            {formatCurrency(
                              ((item.quantity * item.unit_price) - item.discount_value) - (item.quantity * (item.cost_price || 0))
                            )}
                          </div>
                        </td>

                        {/* Delete Button */}
                        <td className="p-3 text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals Summary */}
              {formData.items.length > 0 && (
                <div className="flex justify-end mt-4">
                  <div className="w-96 space-y-2 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal (before discount & tax):</span>
                      <span className="font-medium">
                        {formatCurrency(
                          formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total Discount:</span>
                      <span className="font-medium text-red-600">
                        -{formatCurrency(
                          formData.items.reduce((sum, item) => sum + item.discount_value, 0)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total Tax:</span>
                      <span className="font-medium">
                        {formatCurrency(
                          formData.items.reduce((sum, item) => 
                            sum + (((item.quantity * item.unit_price) - item.discount_value) * (item.tax_rate / 100)), 0
                          )
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-300">
                      <span className="font-semibold">Total Cost:</span>
                      <span className="font-semibold text-yellow-700">
                        {formatCurrency(
                          formData.items.reduce((sum, item) => sum + (item.quantity * (item.cost_price || 0)), 0)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-base pt-2 border-t-2 border-gray-400">
                      <span className="font-bold">Grand Total:</span>
                      <span className="font-bold text-lg">
                        {formatCurrency(
                          formData.items.reduce((sum, item) => 
                            sum + (((item.quantity * item.unit_price) - item.discount_value) * (1 + (item.tax_rate / 100))), 0
                          )
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-base pt-2 border-t border-gray-300">
                      <span className="font-bold text-green-700">Gross Profit:</span>
                      <span className={`font-bold text-lg ${
                        formData.items.reduce((sum, item) => 
                          sum + (((item.quantity * item.unit_price) - item.discount_value) - (item.quantity * (item.cost_price || 0))), 0
                        ) >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {formatCurrency(
                          formData.items.reduce((sum, item) => 
                            sum + (((item.quantity * item.unit_price) - item.discount_value) - (item.quantity * (item.cost_price || 0))), 0
                          )
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Total Duration:</span>
                      <span>
                        {formData.items.reduce((sum, item) => sum + ((item.duration || 0) * item.quantity), 0)} minutes
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="notes_public">Customer Notes</Label>
                <textarea
                  id="notes_public"
                  value={formData.notes_public}
                  onChange={(e) => setFormData({ ...formData, notes_public: e.target.value })}
                  className="w-full min-h-20 px-3 py-2 border rounded-md"
                  placeholder="Notes visible to customer..."
                />
              </div>
              
              <div>
                <Label htmlFor="notes_internal">Internal Notes</Label>
                <textarea
                  id="notes_internal"
                  value={formData.notes_internal}
                  onChange={(e) => setFormData({ ...formData, notes_internal: e.target.value })}
                  className="w-full min-h-20 px-3 py-2 border rounded-md"
                  placeholder="Internal notes (hidden from customer)..."
                />
              </div>
            </div>

            {/* Deposit Settings */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  id="deposit_required"
                  checked={formData.deposit_required}
                  onChange={(e) => setFormData({ ...formData, deposit_required: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="deposit_required">Require Deposit</Label>
              </div>
              
              {formData.deposit_required && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deposit Type</Label>
                    <Select 
                      value={formData.deposit_type} 
                      onValueChange={(value) => setFormData({ ...formData, deposit_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>
                      Deposit Value ({formData.deposit_type === 'percent' ? '%' : '£'})
                    </Label>
                    <Input
                      type="number"
                      step={formData.deposit_type === 'percent' ? "1" : "0.01"}
                      min="0"
                      max={formData.deposit_type === 'percent' ? "100" : undefined}
                      value={formData.deposit_value}
                      onChange={(e) => setFormData({ ...formData, deposit_value: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                {editingQuote ? 'Update Quote' : 'Create Quote'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Quote Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto w-[95vw]">
          <DialogHeader>
            <DialogTitle>
              Quote Details - {selectedQuote?.quote_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-6">
              {/* Quote Header */}
              <div className="grid grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium mb-2">Quote Information</h3>
                  <div className="space-y-1 text-sm">
                    <div>Status: {getStatusBadge(selectedQuote.status)}</div>
                    <div>Total: {formatCurrency(selectedQuote.total, selectedQuote.currency)}</div>
                    <div>Valid Until: {selectedQuote.valid_until ? 
                      new Date(selectedQuote.valid_until).toLocaleDateString() : 'No expiry'
                    }</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">Customer</h3>
                  <div className="text-sm">
                    {customers.find(c => c.id === selectedQuote.customer_id)?.name || 'Unknown Customer'}
                  </div>
                </div>
              </div>

              {/* Quote Items */}
              <div>
                <h3 className="font-medium mb-4">Quote Items</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Discount</TableHead>
                      <TableHead>Tax</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedQuote.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell>{formatCurrency(item.discount_value)}</TableCell>
                        <TableCell>{item.tax_rate}%</TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(item.line_total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                <div className="mt-4 space-y-2 text-right">
                  <div>Subtotal: {formatCurrency(selectedQuote.subtotal)}</div>
                  <div>Tax: {formatCurrency(selectedQuote.tax_total)}</div>
                  <div className="font-bold text-lg">
                    Total: {formatCurrency(selectedQuote.total)}
                  </div>
                  {selectedQuote.deposit_required && (
                    <div className="text-blue-600">
                      Deposit Required: {formatCurrency(selectedQuote.deposit_amount)}
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {(selectedQuote.notes_public || selectedQuote.notes_internal) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedQuote.notes_public && (
                    <div>
                      <h4 className="font-medium mb-2">Customer Notes</h4>
                      <div className="p-3 bg-gray-50 rounded text-sm">
                        {selectedQuote.notes_public}
                      </div>
                    </div>
                  )}
                  
                  {selectedQuote.notes_internal && (
                    <div>
                      <h4 className="font-medium mb-2">Internal Notes</h4>
                      <div className="p-3 bg-yellow-50 rounded text-sm">
                        {selectedQuote.notes_internal}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print View */}
      {isPrintViewOpen && printQuote && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <QuotePrintView
            quote={printQuote}
            onClose={() => {
              setIsPrintViewOpen(false);
              setPrintQuote(null);
            }}
            getAuthHeaders={getAuthHeaders}
          />
        </div>
      )}

      {/* Status Change Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Quote Status</DialogTitle>
            <DialogDescription>
              Update the status for Quote #{selectedQuoteForStatus?.quote_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedQuoteForStatus && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Current Status:</span>
                  {getStatusBadge(selectedQuoteForStatus.status)}
                </div>
                <div className="text-xs text-gray-600">
                  Customer: {customers.find(c => c.id === selectedQuoteForStatus.customer_id)?.name || 'Unknown'}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="new-status">New Status</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {availableStatuses.map((statusOption) => (
                  <Button
                    key={statusOption.status}
                    variant="outline"
                    onClick={() => handleStatusChange(statusOption.status)}
                    className="justify-start h-auto p-3 text-left"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <div className="flex-shrink-0">
                        {getStatusBadge(statusOption.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{statusOption.label}</div>
                        <div className="text-xs text-gray-500">{statusOption.description}</div>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="status-reason">Reason (Optional)</Label>
              <textarea
                id="status-reason"
                value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
                rows="2"
                placeholder="Add a note about this status change..."
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsStatusDialogOpen(false);
                  setSelectedQuoteForStatus(null);
                  setStatusChangeReason('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Search Dialog */}
      <Dialog open={serviceSearchOpen} onOpenChange={setServiceSearchOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Search Services & Products</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder="Search by name, SKU, or description..."
              value={serviceSearchTerm}
              onChange={(e) => setServiceSearchTerm(e.target.value)}
              className="w-full"
              autoFocus
            />
            
            <div className="border rounded-lg max-h-[60vh] overflow-y-auto">
              {services
                .filter(service => {
                  if (!serviceSearchTerm) return true;
                  const search = serviceSearchTerm.toLowerCase();
                  return (
                    service.name.toLowerCase().includes(search) ||
                    service.sku?.toLowerCase().includes(search) ||
                    service.description?.toLowerCase().includes(search) ||
                    service.category?.toLowerCase().includes(search)
                  );
                })
                .map(service => (
                  <div
                    key={service.id}
                    className="p-4 border-b hover:bg-gray-50 cursor-pointer flex justify-between items-start"
                    onClick={() => {
                      if (selectedLineIndex !== null) {
                        const newItems = [...formData.items];
                        newItems[selectedLineIndex] = {
                          ...newItems[selectedLineIndex],
                          service_id: service.id,
                          sku: service.sku || '',
                          description: service.description || service.name,
                          unit_price: service.default_price || 0,
                          cost_price: service.cost || 0,
                          duration: service.default_duration_minutes || null
                        };
                        setFormData({ ...formData, items: newItems });
                        setServiceSearchOpen(false);
                        setServiceSearchTerm('');
                      }
                    }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{service.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {service.sku}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {service.category}
                        </Badge>
                      </div>
                      {service.description && (
                        <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                      )}
                      <div className="flex gap-4 text-xs text-gray-500">
                        <span>Price: <strong>{formatCurrency(service.default_price || 0)}</strong></span>
                        {service.cost && (
                          <span>Cost: <strong>{formatCurrency(service.cost)}</strong></span>
                        )}
                        {service.default_duration_minutes && (
                          <span>Duration: <strong>{service.default_duration_minutes} min</strong></span>
                        )}
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      Add
                    </Button>
                  </div>
                ))}
              
              {services.filter(service => {
                if (!serviceSearchTerm) return true;
                const search = serviceSearchTerm.toLowerCase();
                return (
                  service.name.toLowerCase().includes(search) ||
                  service.sku?.toLowerCase().includes(search) ||
                  service.description?.toLowerCase().includes(search)
                );
              }).length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  No services found matching "{serviceSearchTerm}"
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Quote</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this quote? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {quoteToDelete && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 text-red-800">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Quote to be deleted:</span>
                </div>
                <div className="mt-2 text-sm text-red-700">
                  <div>Quote #: {quoteToDelete.quote_number}</div>
                  <div>Customer: {customers.find(c => c.id === quoteToDelete.customer_id)?.name || 'Unknown'}</div>
                  <div>Total: {formatCurrency(quoteToDelete.total, quoteToDelete.currency)}</div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteConfirmOpen(false);
                    setQuoteToDelete(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteQuote}
                  className="flex-1"
                >
                  Delete Quote
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Status Dialog */}
      <ChangeStatusDialog
        open={changeStatusDialogOpen}
        onOpenChange={setChangeStatusDialogOpen}
        currentStatus={quoteToChangeStatus?.status}
        availableStatuses={quoteStatuses}
        onConfirm={handleChangeStatus}
        itemType="quote"
      />
    </div>
  );
};

export default QuotesManagement;